import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as courseService from '../services/courseService'; 
import * as accessCodeService from '../services/accessCodeService';
import { 
    Box, Typography, TextField, Button, List, ListItem, ListItemText, IconButton, 
    CircularProgress, Alert, Divider, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Switch, Tooltip 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const AdminConfig = () => {
  // --- STATO ---
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [errorCategories, setErrorCategories] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [errorCourses, setErrorCourses] = useState('');
  const [newCourseName, setNewCourseName] = useState('');

  const [accessCodes, setAccessCodes] = useState([]);
  const [loadingAccessCodes, setLoadingAccessCodes] = useState(true);
  const [errorAccessCodes, setErrorAccessCodes] = useState('');
  const [newCodeDescription, setNewCodeDescription] = useState('');

  // --- FUNZIONI PER LE CATEGORIE ---
  const fetchCategories = () => {
    setLoadingCategories(true);
    axios.get('/api/categories')
      .then(res => setCategories(res.data))
      .catch(err => setErrorCategories('Impossibile caricare le categorie.'))
      .finally(() => setLoadingCategories(false));
  };

  const handleCreateCategory = () => {
    axios.post('/api/admin/categories', { name: newCategoryName })
      .then(() => {
        setNewCategoryName('');
        fetchCategories();
      })
      .catch(err => setErrorCategories(err.response?.data?.error || 'Errore nella creazione della categoria.'));
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa categoria?')) {
      axios.delete(`/api/admin/categories/${categoryId}`)
        .then(() => fetchCategories())
        .catch(err => setErrorCategories('Errore nell\'eliminazione della categoria.'));
    }
  };

  const handleUpdateCategory = (categoryId) => {
    const newName = prompt('Inserisci il nuovo nome per la categoria:');
    if (newName && newName.trim() !== '') {
        axios.put(`/api/admin/categories/${categoryId}`, { name: newName })
            .then(() => fetchCategories())
            .catch(err => setErrorCategories(err.response?.data?.error || 'Errore nell\'aggiornamento della categoria.'));
    }
  };
  
  // --- FUNZIONI PER I CORSI ---
  const fetchCourses = () => {
    setLoadingCourses(true);
    courseService.getAllCourses()
      .then(data => setCourses(data))
      .catch(err => setErrorCourses('Impossibile caricare i corsi.'))
      .finally(() => setLoadingCourses(false));
  };
  
  const handleCreateCourse = () => {
    courseService.createCourse({ name: newCourseName })
      .then(() => {
        setNewCourseName('');
        fetchCourses();
      })
      .catch(err => setErrorCourses(err.response?.data?.error || 'Errore nella creazione del corso.'));
  };

  const handleDeleteCourse = (courseId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo corso?')) {
      courseService.deleteCourse(courseId)
        .then(() => fetchCourses())
        .catch(err => setErrorCourses('Errore nell\'eliminazione del corso.'));
    }
  };

  const handleUpdateCourse = (courseId) => {
    const newName = prompt('Inserisci il nuovo nome per il corso:');
    if (newName && newName.trim() !== '') {
      courseService.updateCourse(courseId, { name: newName })
        .then(() => fetchCourses())
        .catch(err => setErrorCourses(err.response?.data?.error || 'Errore nell\'aggiornamento del corso.'));
    }
  };

  // --- FUNZIONI PER I CODICI DI ACCESSO ---
  const fetchAccessCodes = () => {
    setLoadingAccessCodes(true);
    accessCodeService.getAllAccessCodes()
      .then(data => setAccessCodes(data))
      .catch(err => setErrorAccessCodes('Impossibile caricare i codici.'))
      .finally(() => setLoadingAccessCodes(false));
  };

  const handleCreateAccessCode = () => {
    accessCodeService.createAccessCode(newCodeDescription)
      .then(() => {
        setNewCodeDescription('');
        fetchAccessCodes();
      })
      .catch(err => setErrorAccessCodes(err.response?.data?.error || 'Errore nella creazione del codice.'));
  };

  const handleToggleCodeStatus = (id, currentStatus) => {
    accessCodeService.updateAccessCode(id, { isActive: !currentStatus })
      .then(() => fetchAccessCodes())
      .catch(err => setErrorAccessCodes('Errore nell\'aggiornamento dello stato.'));
  };
  
  const handleDeleteAccessCode = (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo codice?')) {
        accessCodeService.deleteAccessCode(id)
        .then(() => fetchAccessCodes())
        .catch(err => setErrorAccessCodes('Errore nell\'eliminazione del codice.'));
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCourses();
    fetchAccessCodes();
  }, []);

  if (loadingCategories || loadingCourses || loadingAccessCodes) return <CircularProgress />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Configurazione Piattaforma
      </Typography>
      
      {/* SEZIONE GESTIONE CATEGORIE */}
      <Box component="div" sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>Gestione Categorie (Competenze)</Typography>
        {errorCategories && <Alert severity="error" sx={{ my: 2 }}>{errorCategories}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField 
            label="Nuova Categoria" 
            variant="outlined" 
            size="small"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
          <Button variant="contained" onClick={handleCreateCategory}>Aggiungi</Button>
        </Box>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {categories.map(cat => (
            <ListItem key={cat.id} divider secondaryAction={
              <>
                <IconButton edge="end" aria-label="edit" onClick={() => handleUpdateCategory(cat.id)}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCategory(cat.id)} sx={{ ml: 1 }}>
                  <DeleteIcon />
                </IconButton>
              </>
            }>
              <ListItemText primary={cat.name} />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* SEZIONE GESTIONE CORSI */}
      <Box component="div" sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>Gestione Corsi</Typography>
        {errorCourses && <Alert severity="error" sx={{ my: 2 }}>{errorCourses}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField 
            label="Nuovo Corso" 
            variant="outlined" 
            size="small"
            value={newCourseName}
            onChange={(e) => setNewCourseName(e.target.value)}
          />
          <Button variant="contained" onClick={handleCreateCourse}>Aggiungi</Button>
        </Box>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {courses.map(course => (
            <ListItem key={course.id} divider secondaryAction={
              <>
                <IconButton edge="end" aria-label="edit" onClick={() => handleUpdateCourse(course.id)}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCourse(course.id)} sx={{ ml: 1 }}>
                  <DeleteIcon />
                </IconButton>
              </>
            }>
              <ListItemText primary={course.name} />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* SEZIONE GESTIONE CODICI DI ACCESSO */}
      <Box component="div" sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>Gestione Codici di Accesso</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crea e gestisci i codici univoci per permettere la registrazione degli alunni.
        </Typography>
        {errorAccessCodes && <Alert severity="error" sx={{ my: 2 }}>{errorAccessCodes}</Alert>}
        
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <TextField 
            label="Descrizione (es. 'Classe Giardinieri 2025')" 
            variant="outlined" 
            size="small"
            fullWidth
            value={newCodeDescription}
            onChange={(e) => setNewCodeDescription(e.target.value)}
          />
          <Button variant="contained" onClick={handleCreateAccessCode}>Genera Codice</Button>
        </Box>

        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Codice</TableCell>
                        <TableCell>Descrizione</TableCell>
                        <TableCell>Utilizzi</TableCell>
                        <TableCell>Attivo</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {accessCodes.map((code) => (
                        <TableRow key={code.id}>
                            <TableCell>
                                <Typography component="span" fontFamily="monospace">{code.code}</Typography>
                                <Tooltip title="Copia codice">
                                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(code.code)}>
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                            <TableCell>{code.description}</TableCell>
                            <TableCell>{code.usageCount}</TableCell>
                            <TableCell>
                                <Switch
                                    checked={code.isActive}
                                    onChange={() => handleToggleCodeStatus(code.id, code.isActive)}
                                    color="success"
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip title="Elimina codice">
                                    <IconButton size="small" onClick={() => handleDeleteAccessCode(code.id)} color="error">
                                        <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
      </Box>

    </Box>
  );
};

export default AdminConfig;